import * as _ from 'lodash';
import * as utils from 'common/utils';
import { toastr } from 'react-redux-toastr';
import { toggleShowSettingInSession } from 'home/AssetHierarchy/actions/assetDetails.actions';
import { OVERLAY_TRACE_KEYS, getOverlayTraceKeysForChartType, isOverlayPersistentChartType } from 'utils/overlayTraceUtils';
import { OVERLAY_POINTS_LIMIT, getOverlayPointLimitExceededMessage } from 'common/constants';
import { preferredSpectrumXUnit } from 'common/utils/spectrumDisplay';
import { convertOverlayPointsYUnit } from './overlaySpectrumConversion';
import {
  determineXAxisUnit,
  getPointsStateUpdate,
  prepareSelectedPointsUpdate
} from './overlayPointSelection';

export function createChartsViewOverlayCallbacks({
  getState,
  setState,
  getProps,
  getShaftSpeed,
  setChartSettingsInSession,
  fetchOverlaySpectrumForPoint,
  fetchOverlayWaveformForPoint,
  ensureOverlayTraceDataForChartType,
  updatePreviewConfig,
  getSpectrumMarkers,
  getFilterCriteria
}) {
  const getWaveformAndSpectrum = (newparams) => {
    const { amp_type, feature, tag, config, logScale, showOverlay, spectrumXUnit } = getState();
    const { getTagSpectrum, getTagWaveform, getTagCepstrum } = getProps().assetDetailsActions;
    if (!tag || tag.structure_type !== 'vector' || tag.type === 'utilization') return;

    let params = {
      feature,
      amp_type,
      ...newparams
    };
    params = utils.sanitizeAmpTypeParam(params, tag);

    if (newparams.fromPointSelection) {
      const selection = { preserveXUnit: newparams.preserveXUnit };
      params.currentXUnit = determineXAxisUnit(selection, config, showOverlay, spectrumXUnit);
      params.preserveXUnit = newparams.preserveXUnit;
    }

    if (params.amp_type === 'demod') {
      params.amp_type = 'acceleration';
      params.type = 'demod';
    }
    if (params.amp_type === 'normal') params.amp_type = 'velocity';

    if (tag.type === 'current' && _.isNil(params.log_scale)) params.log_scale = logScale;

    if (!config.spectrumFeatures) {
      getProps().spectrumFeatureActions.getSpectrumFeatures(tag.id, config.config_id);
    }

    if (getProps().previewConfig) {
      updatePreviewConfig(tag, params);
      getSpectrumMarkers(params.timestamp, config, tag);
      return;
    }

    if (showOverlay && params.timestamp) {
      const traceKeys = getOverlayTraceKeysForChartType(getState().chartType);

      if (traceKeys.indexOf(OVERLAY_TRACE_KEYS.SPECTRUM) !== -1) {
        fetchOverlaySpectrumForPoint(params);
      }
      // Skip the waveform fetch for spectrum-only overlays — the response isn't consumed.
      if (traceKeys.indexOf(OVERLAY_TRACE_KEYS.WAVEFORM) !== -1) {
        fetchOverlayWaveformForPoint(params);
      }
    } else {
      getTagSpectrum(tag.id, params, config.config_id).then((response) => {
        setState(prevState => ({
          config: {
            ...prevState.config,
            spectrum: {
              ...prevState.config.spectrum,
              spectrum_data: response.spectrum_data,
              freq_units: response.freq_units,
              amp_units: response.amp_units,
              fmax: response.fmax,
              lines_of_resolution: response.lines_of_resolution,
              shaft_speed: response.shaft_speed
            },
            originalSpectrumFeatures: prevState.config.originalSpectrumFeatures ||
              (config.spectrumFeatures ? _.cloneDeep(config.spectrumFeatures) : null)
          }
        }));
      });
    }

    if (!(showOverlay && params.timestamp)) {
      getTagWaveform(tag.id, params, config.config_id);
    }

    if (tag.type === 'vibration') {
      getTagCepstrum(tag.id, params, config.config_id);
    }

    getSpectrumMarkers(params.timestamp, config, tag);
  };

  const debouncedGetWaveformAndSpectrum = _.debounce((selection) => {
    const { config, showOverlay, spectrumXUnit } = getState();
    const currentXUnit = determineXAxisUnit(selection, config, showOverlay, spectrumXUnit);
    getWaveformAndSpectrum({
      timestamp: selection.x,
      currentXUnit,
      preserveXUnit: selection.preserveXUnit,
      fromPointSelection: true
    });
  }, 500);

  const toggleSpectrumYUnit = (newYUnit) => {
    if (!newYUnit || newYUnit === getState().spectrumYUnit) return;

    const { selectedPoints, showOverlay, config } = getState();
    const prevYUnit = getState().spectrumYUnit ||
      (getState().config && getState().config.spectrum &&
        getState().config.spectrum.amp_units) || '';

    if (showOverlay && selectedPoints && selectedPoints.length > 0) {
      const updatedPoints = convertOverlayPointsYUnit(
        selectedPoints,
        newYUnit,
        prevYUnit,
        config,
        getShaftSpeed
      );
      setState({
        selectedPoints: updatedPoints,
        spectrumYUnit: newYUnit
      });
    } else {
      setState({ spectrumYUnit: newYUnit });
    }
  };

  const setChartType = (chartType) => {
    if (chartType !== getState().chartType) {
      const { showOverlay, chartType: prevChartType } = getState();
      const preserveOverlayPoints = showOverlay &&
        isOverlayPersistentChartType(prevChartType) &&
        isOverlayPersistentChartType(chartType);

      if (preserveOverlayPoints) {
        setState({ chartType }, () => {
          setChartSettingsInSession();
          ensureOverlayTraceDataForChartType(chartType);
        });
      } else if (showOverlay) {
        setState({
          chartType,
          selectedPoints: []
        }, () => setChartSettingsInSession());
      } else {
        setState({ chartType }, () => setChartSettingsInSession());
      }
    }
  };

  const isOverlayMessageDismissed = () => localStorage.getItem('overlayInstructionMessageDismissed') === 'true';

  const handleOverlayMessageDismiss = (dontShowAgain) => {
    setState({ showOverlayNote: false });

    if (dontShowAgain) {
      localStorage.setItem('overlayInstructionMessageDismissed', 'true');
      toastr.success(getProps().t('assetHierarchy.charts.overlayInstructionDismissed', {
        defaultValue: 'Overlay instruction message will no longer be shown'
      }));
    }
  };

  const toggleShowOverlay = () => {
    setState((prevState) => {
      const newState = {
        showOverlay: !prevState.showOverlay,
        showOverlayNote: !prevState.showOverlay && !isOverlayMessageDismissed()
      };

      if (prevState.showOverlay) {
        // Disabling overlay: restore the user's preferred frequency unit rather than
        // forcing 'Hz'. 'Orders' preference => 'Orders', otherwise null (follow the
        // backend/preference unit). Forcing 'Hz' here dropped a CPM/Orders preference
        // until reload (the SC-36397 class of unit drift). Derivation downstream
        // (deriveSpectrumDisplay) resolves null back to the backend unit safely.
        const { currentUser } = getProps();
        newState.spectrumXUnit = preferredSpectrumXUnit(currentUser);

        if (prevState.selectedPoints && prevState.selectedPoints.length > 0) {
          const lastPoint = prevState.selectedPoints[prevState.selectedPoints.length - 1];
          newState.selectedPoints = [];
          newState.waveAndSpecSelection = lastPoint;
        } else {
          newState.selectedPoints = [];
        }
      } else if (!prevState.showOverlay) {
        // Only default to Orders if the user hasn't already picked a unit.
        if (!prevState.spectrumXUnit &&
          prevState.chartType === 'spectrum' && prevState.config && prevState.config.spectrum) {
          const shaftSpeed = getShaftSpeed();
          if (shaftSpeed && shaftSpeed > 0.001) {
            newState.spectrumXUnit = 'Orders';
          }
        }
      }

      return newState;
    }, () => {
      toggleShowSettingInSession('showOverlay');
      const { showOverlay, selectedPoints, chartType } = getState();
      if (showOverlay && selectedPoints.length > 0) {
        ensureOverlayTraceDataForChartType(chartType);
      }
    });
  };

  const updateSelectedPoints = (newPoints) => {
    if (newPoints.length > OVERLAY_POINTS_LIMIT) {
      toastr.warning(getProps().t('charts.overlay.pointSelectionLimitTitle', {
        defaultValue: 'Point Selection Limit'
      }), getOverlayPointLimitExceededMessage(getProps().t));
      return;
    }

    const shaftSpeed = getShaftSpeed();
    const filterCriteria = getFilterCriteria();

    const { finalPoints, pointsWithColors: initialPointsWithColors } = prepareSelectedPointsUpdate(
      newPoints,
      getState().selectedPoints,
      filterCriteria
    );

    let pointsWithColors = initialPointsWithColors;
    if (finalPoints.length > OVERLAY_POINTS_LIMIT) {
      toastr.warning(getProps().t('charts.overlay.pointSelectionLimitTitle', {
        defaultValue: 'Point Selection Limit'
      }), getOverlayPointLimitExceededMessage(getProps().t));
      pointsWithColors = pointsWithColors.slice(0, OVERLAY_POINTS_LIMIT);
    }

    setState(prevState => getPointsStateUpdate(
      pointsWithColors,
      prevState.showOverlay,
      prevState.spectrumXUnit,
      shaftSpeed
    ));
  };

  return {
    getWaveformAndSpectrum,
    debouncedGetWaveformAndSpectrum,
    toggleSpectrumYUnit,
    setChartType,
    isOverlayMessageDismissed,
    handleOverlayMessageDismiss,
    toggleShowOverlay,
    updateSelectedPoints
  };
}
